import { AccountInfo } from "@azure/msal-browser";
import { authService } from "./authService";

class UserProfileService {
  private photoCache = new Map<string, { url: string; refCount: number }>();

  /**
   * Récupère la photo de profil de l'utilisateur depuis Microsoft Graph
   * Utilise un système de comptage de références pour gérer le cleanup des blobs
   * @param account - Compte utilisateur MSAL contenant l'ID et les infos de tenant
   * @returns URL blob de la photo ou null si non disponible
   */
  async getUserPhoto(account: AccountInfo): Promise<string | null> {
    const userId = account.username;
    const cacheKey = userId;

    // Vérifier le cache
    const cached = this.photoCache.get(cacheKey);
    if (cached) {
      cached.refCount++;
      return cached.url;
    }

    try {
      const token = await authService.getAccessToken(account);

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Pas de photo de profil définie
          return null;
        }
        throw new Error(`HTTP error ${response.status}`);
      }

      const blob = await response.blob();
      const photoUrl = URL.createObjectURL(blob);

      // Mettre en cache avec compteur de références
      this.photoCache.set(cacheKey, { url: photoUrl, refCount: 1 });

      return photoUrl;
    } catch (error) {
      console.warn("Impossible de récupérer la photo de profil:", error);
      return null;
    }
  }

  /**
   * Récupère les informations du profil utilisateur
   * @param account - Compte utilisateur MSAL
   * @returns Informations du profil ou null en cas d'erreur
   */
  async getUserProfile(account: AccountInfo) {
    try {
      const token = await authService.getAccessToken(account);
      const userId = account.localAccountId;

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/users/${userId}?$select=displayName,mail,userPrincipalName,jobTitle`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Erreur lors de la récupération du profil:", error);
      return null;
    }
  }

  /**
   * Libère une référence à une photo
   * Révoque l'URL blob si plus aucune référence
   */
  releasePhoto(userId: string) {
    const cached = this.photoCache.get(userId);
    if (!cached) return;

    cached.refCount--;
    if (cached.refCount <= 0) {
      URL.revokeObjectURL(cached.url);
      this.photoCache.delete(userId);
    }
  }

  /**
   * Nettoie le cache des photos (force cleanup)
   */
  clearPhotoCache() {
    // Révoquer les URLs blob pour libérer la mémoire
    this.photoCache.forEach(({ url }) => {
      URL.revokeObjectURL(url);
    });
    this.photoCache.clear();
  }
}

export const userProfileService = new UserProfileService();
